import { useCallback, useEffect, useState } from "react";
import MenuTop from "../../../components/menu/top/menu-top";
import storage from "../../../shared/services/storage";
import Form from "../../../components/form/form";
import financialFormData from "../../../features/financial/forms/financial-form-data";
import { fetchTiposLancamento } from "../../../features/lancamento/service/tipo-lancamento";
import InfoComponent from "../../../components/info/info";
import { FormProps } from "../../../shared/interfaces/form";
import { fetchRubricasGrouped } from "../../../features/rubrica/service/rubrica";
import { User } from "../../../shared/interfaces/user";
import { checkPaymentMatchService, fetchFinancialsByProjectIdService, setFinancialData, updatePaymentDateService } from "../../../features/financial/service/financeiro-service";
import { SEVERITY } from "../../../shared/interfaces/alert";
import { useNavigate, useParams } from "react-router-dom";
import { useAlert } from "../../../components/alert/alert";
import './style.css';
import { fetchBeneficiariesByIdCompanyService, fetchBeneficiariesService } from "../../../features/beneficiary/service/beneficiary-service";
import formatDateToISO from "../../../shared/utils/data-formatter";

function Financial() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [formData, setFormData] = useState<FormProps[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const getTiposLancamento = useCallback(async () => {
        try {
            const lancamentos = await fetchTiposLancamento();
            return lancamentos?.map(({ name, id}: any) => ({ label: name, value: id }));
        } catch (error) {
            console.error(error);
        }
    }, []);


    const getRubricasGrouped = useCallback(async () => {
        try {
            return await fetchRubricasGrouped();;
        } catch (error) {
            console.error(error);
        }
    }, []);

    const getProjectsByFinancier = useCallback(async () => {
        const userData: User =  await JSON.parse(storage.getStorage('user') || '');

        try {
            const projects = await fetchFinancialsByProjectIdService(userData.company.id as string);
            return projects?.map(({ name, id}: any) => ({ label: name, value: id }));
        } catch (error) {
            console.error(error);
        }
    }, []);

    const getBeneficiaries = useCallback(async () => {
        const userData: User =  await JSON.parse(storage.getStorage('user') || '');
        try {
            if (userData.company?.name?.toLowerCase() === 'causale') {
                return await fetchBeneficiariesService();
            }

            return await fetchBeneficiariesByIdCompanyService(userData.company.id as string);
        } catch (error) {
            console.error(error);
        }
    }, []);

    const setFormValues = useCallback( async () => {
        setIsLoading(true);
        try {
            const response = { idProject: id }
            const shouldShowProjectSelect = id ? false : true;

            const [valueLancamentos, valueRubricasGrouped, valueProjects, valueBeneficiaries] = await Promise.all([
                getTiposLancamento(),
                getRubricasGrouped(),
                getProjectsByFinancier(),
                getBeneficiaries()
            ]);

            setFormData(financialFormData(undefined, response, valueLancamentos, valueRubricasGrouped, valueProjects, valueBeneficiaries, shouldShowProjectSelect));
        } catch (error) {
            console.error("Erro ao carregar dados do formulário:", error);
            showAlert('Erro ao carregar dados do formulário', SEVERITY.ERROR);
        } finally {
            setIsLoading(false);
        }
    }, [getTiposLancamento, getRubricasGrouped, getProjectsByFinancier, getBeneficiaries, id, showAlert]);

    useEffect(() => {
        setFormValues();
    }, [setFormValues]);

    const onSubmitForm = async (data: any) => {
        setIsLoading(true);
        
        try {
            // Converter strings vazias para null para evitar erros de UUID inválido
            const updatedData = {
                ...data,
                idProject: id ? id : data.idProject,
                valor: Math.round(parseFloat(data.valor) * 100),
                idBeneficiary: data.idBeneficiary && data.idBeneficiary.trim() !== '' ? data.idBeneficiary : null,
                description: data.description && data.description.trim() !== '' ? data.description : null,
                recipt: data.recipt || null
            };
    
            const financialDataAddedFormatted = formatDateToISO(data.financialDateAdded);
            updatedData.financialDateAdded = financialDataAddedFormatted;
    
            // 1. SEMPRE criar a movimentação financeira primeiro (nunca bloquear isso)
            const createResult = await setFinancialData(updatedData, null);
    
            // 2. VERIFICAR se é despesa com beneficiário para processamento automático
            if (updatedData.idBeneficiary && updatedData.idProject) {
                try {
                    // Buscar dados do tipo de lançamento para verificar se é despesa
                    const tipoLancamento = createResult.lancamento;
                    const isDespesa = tipoLancamento?.name?.toLowerCase().includes('despesa') || 
                                     tipoLancamento?.description?.toLowerCase().includes('despesa');
    
                    if (isDespesa) {
                        // Verificar correspondência usando service
                        const checkResult = await checkPaymentMatchService(
                            updatedData.idProject,
                            updatedData.idBeneficiary,
                            updatedData.valor
                        );
    
                        if (checkResult.match === true) {
                            // Atualizar data de pagamento automaticamente usando service
                            const updateResult = await updatePaymentDateService(
                                updatedData.idProject,
                                updatedData.idBeneficiary,
                                updatedData.valor
                            );
    
                            // Mostrar feedback adicional ao usuário
                            setTimeout(() => {
                                showAlert(
                                    `Pagamento automaticamente registrado para ${updateResult.data?.beneficiary?.name || 'beneficiário'}!`, 
                                    SEVERITY.SUCCESS
                                );
                            }, 1500);
                        }
                    }
    
                } catch (autoError) {
                    // Erro no processamento automático NÃO deve afetar o sucesso da criação
                    // Movimento foi criado com sucesso mesmo se o processamento automático falhar
                }
            }
    
            // 3. SEMPRE mostrar sucesso da criação principal
            showAlert('Dados financeiros salvos com sucesso', SEVERITY.SUCCESS);
            navigate(`/projeto/${id}`, { replace: true });
            
        } catch (error: any) {
            showAlert(
                error.message || 'Erro ao salvar dados financeiros', 
                SEVERITY.ERROR
            );
        } finally { 
            setIsLoading(false);
        }
    }

    return (
        <div className="container-add-financial">
            <MenuTop />
            <InfoComponent />
            <div className="container-add-financial__container">
                <Form
                    data={formData}
                    title="Cadastrar nova movimentação financeira"
                    onSubmitForm={onSubmitForm}
                    onCancel={() => navigate(`/projeto-list/${id}`, { replace: true })}
                    isLoading={isLoading}
                />
            </div>
        </div>
    );
}

export default Financial;